import { add } from 'date-fns';
import { getEventSheetsFuse, getSheetEventTitle } from 'pages/ScheduleV6/utils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectVisibleSheetsForDate } from 'store/schedule/scheduleSelectors';

const getSheetHeightInPixels = (sheet, title) => {
  const defaultHeight = 18 + 6 + 4 + 2;

  const maxLineLength = 21;
  
  const jobNumberLines = Math.ceil((sheet?.project?.jobNumber?.length || 0) / maxLineLength);
  const jobNumberHeight = (jobNumberLines * 15);

  const titleLines = Math.ceil((title?.length || 0) / maxLineLength);
  const titleHeight = (titleLines * 22);

  const contractorsLines = Math.ceil((sheet?.project?.contractor?.name?.length || 0) / maxLineLength);
  const contractorsHeight = (contractorsLines * 12);

  // Improve calculation: add cancel block and notes block
  const totalHeight =
    defaultHeight +
    titleHeight +
    contractorsHeight +
    jobNumberHeight;

  return totalHeight;
}

/**
 * Maps adapted schedule sheets from the store into FullCalendar v6
 * event objects for time-based views (daily/weekly).
 *
 * For now we:
 * - reuse adapted data from schedulePageOperation (start/end, originalStart/originalEnd);
 * - attach the full adapted sheet object into extendedProps so event
 *   renderers (later ScheduleEventV6) have everything they need;
 * - filter sheets to the current day to avoid rendering irrelevant events.
 */
export const useV6CrewLeadersEvents = () => {
  const allSheets = useSelector(selectVisibleSheetsForDate);
  const { searchValue, showAllLocations } = useSelector((store) => store.schedule);
  const locations = useSelector((state) => state.personalProfile.activeLocations);
  
  const visibleSheets = useMemo(() => {
    const fuse = getEventSheetsFuse(allSheets);
    if (searchValue) return fuse.search(searchValue);
    return structuredClone(allSheets);
  }, [allSheets, searchValue]);

  const events = useMemo(() => {
    if (!Array.isArray(visibleSheets) || !visibleSheets.length) return [];

    return visibleSheets.map((entry) => {
      const { sheet, start } = entry;
      const title = getSheetEventTitle(entry, 'short');
      const locationColor = showAllLocations
        ? locations.find(({ _id }) => sheet.tenantLocationId === _id)?.color
        : null;

      const height = getSheetHeightInPixels(sheet, title);
      const heightInMinutes = (1440 * height) / (1080 * 0.988);
      const heightEnd = add(new Date(start), { minutes: heightInMinutes });

      let renderEnd = heightEnd;

      return {
        id: sheet._id,
        title: title,
        start,
        end: renderEnd,
        extendedProps: {
          ...entry,
          height,
          heightInMinutes,
          heightEnd,
          heightStart: new Date(start),
          locationColor,
        },
      };
    });
  }, [visibleSheets, showAllLocations, locations]).sort((a, b) => {
    const startDiff = new Date(a.start).getTime() - new Date(b.start).getTime();
    const endDiff = new Date(b.start).getTime() - new Date(a.start).getTime();
    return startDiff || endDiff;
  });

  return events;
};

export default useV6CrewLeadersEvents;

